import React, { useState, useMemo, useCallback, useEffect } from 'react'
import { subDays } from 'date-fns'
import { toDateString } from '@/v2/utils/dateHelpers'
import { usePerformanceMetrics } from '@/v2/hooks/usePerformanceMetrics'
import { useDailyPerformance } from '@/v2/hooks/useDailyPerformance'
import { useTheme } from '@mui/material/styles'
import { ThemeProvider } from 'styled-components'
import CircularProgress from '@mui/material/CircularProgress'
import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import RefreshIcon from '@mui/icons-material/Refresh'
import CancelIcon from '@mui/icons-material/Cancel'
import {
  exportToCSV,
  prepareDailyData,
  prepareWeeklyData,
  prepareMonthlyData,
  prepareUTMData,
  prepareStartsData,
} from '@/v2/utils/csvExport'
import {
  DashboardHeader,
  DateRangeSelector,
  MetricCards,
  DailyPerformanceTable,
  WeeklyPerformanceTable,
  MonthlyPerformanceTable,
  UTMPerformanceSimple,
  RecentStartsSimple,
  DashboardLegend,
  UTMFilters,
} from './components'
import { DashboardContainer, LoadingContainer, Title } from './styles'

const PerformanceDashboard = () => {
  const theme = useTheme()

  const initialStartDate = useMemo(
    () => toDateString(subDays(new Date(), 30)),
    []
  )
  const initialEndDate = useMemo(() => toDateString(new Date()), [])

  const [startDate, setStartDate] = useState(initialStartDate)
  const [endDate, setEndDate] = useState(initialEndDate)
  const [utmSource, setUtmSource] = useState('')
  const [utmMedium, setUtmMedium] = useState('')
  const [utmCampaign, setUtmCampaign] = useState('')
  const [dailyPage, setDailyPage] = useState(1)

  const { metrics, isLoading, isError, error, refetch } = usePerformanceMetrics(
    startDate,
    endDate,
    {
      utmSource,
      utmMedium,
      utmCampaign,
    }
  )

  const { dailyData } = useDailyPerformance(startDate, endDate, dailyPage, {
    utmSource,
    utmMedium,
    utmCampaign,
  })

  // Reset to the first page whenever the date range or filters change so we
  // don't land on a page that no longer exists for the new result set.
  useEffect(() => {
    setDailyPage(1)
  }, [startDate, endDate, utmSource, utmMedium, utmCampaign])

  const handleClearFilters = useCallback(() => {
    setUtmSource('')
    setUtmMedium('')
    setUtmCampaign('')
  }, [])

  const starts = metrics?.starts_timeline || []
  const handleExportDaily = () => {
    if (dailyData?.data) {
      const data = prepareDailyData(dailyData.data)
      const headers = Object.keys(data[0] || {})
      exportToCSV(data, headers, 'daily_performance')
    }
  }

  const handleExportWeekly = () => {
    if (metrics?.weekly_data) {
      const data = prepareWeeklyData(metrics.weekly_data)
      const headers = Object.keys(data[0] || {})
      exportToCSV(data, headers, 'weekly_performance')
    }
  }

  const handleExportMonthly = () => {
    if (metrics?.monthly_data) {
      const data = prepareMonthlyData(metrics.monthly_data)
      const headers = Object.keys(data[0] || {})
      exportToCSV(data, headers, 'monthly_performance')
    }
  }

  const handleExportUTM = () => {
    if (metrics?.utm_breakdown) {
      const data = prepareUTMData(metrics.utm_breakdown)
      const headers = Object.keys(data[0] || {})
      exportToCSV(data, headers, 'utm_performance')
    }
  }

  const handleExportStarts = () => {
    if (starts.length > 0) {
      const data = prepareStartsData(starts)
      const headers = Object.keys(data[0] || {})
      exportToCSV(data, headers, 'recent_starts')
    }
  }

  if (isError) {
    return (
      <ThemeProvider theme={theme.palette}>
        <DashboardContainer>
          <Title variant="h4" gutterBottom>
            Performance Dashboard
          </Title>
          <Paper sx={{ p: 4, borderRadius: 4, textAlign: 'center' }}>
            <CancelIcon
              sx={theme => ({
                fontSize: 48,
                color: theme.palette.error?.main || '#FC7777',
                mb: 2,
              })}
            />
            <Typography variant="h6" gutterBottom>
              Unable to Load Data
            </Typography>
            <Typography color="error" gutterBottom>
              {error?.message ||
                'Error loading performance metrics. Please try again later.'}
            </Typography>
            <Button
              variant="contained"
              startIcon={<RefreshIcon />}
              onClick={() => refetch()}
              sx={{ mt: 3, borderRadius: 2 }}
            >
              Retry
            </Button>
          </Paper>
        </DashboardContainer>
      </ThemeProvider>
    )
  }

  const conversionRate =
    metrics?.summary?.conversion_rate ??
    (metrics?.summary?.total_bookings > 0
      ? (
          (metrics.summary.total_starts / metrics.summary.total_bookings) *
          100
        ).toFixed(1)
      : 0)

  return (
    <ThemeProvider theme={theme.palette}>
      <DashboardContainer>
        <DashboardHeader onRefresh={refetch} />

        <Paper
          elevation={0}
          sx={{
            p: 2,
            mb: 3,
            backgroundColor: 'white',
            borderRadius: 2,
            border: '1px solid #e0e0e0',
          }}
        >
          <DateRangeSelector
            startDate={startDate}
            endDate={endDate}
            onStartDateChange={setStartDate}
            onEndDateChange={setEndDate}
          />
          <Box sx={{ mt: 2 }}>
            <UTMFilters
              utmSource={utmSource}
              utmMedium={utmMedium}
              utmCampaign={utmCampaign}
              onSourceChange={setUtmSource}
              onMediumChange={setUtmMedium}
              onCampaignChange={setUtmCampaign}
              onClearFilters={handleClearFilters}
              utmFilterOptions={metrics?.utm_filter_options}
              patientData={metrics?.utm_breakdown_by_patients}
              appointmentData={metrics?.utm_breakdown}
              startsData={metrics?.starts_timeline}
            />
          </Box>
        </Paper>

        {isLoading ? (
          <LoadingContainer>
            <CircularProgress size={48} thickness={4} />
            <Typography variant="h6" color="textSecondary">
              Loading performance metrics...
            </Typography>
          </LoadingContainer>
        ) : (
          <>
            <MetricCards metrics={metrics} conversionRate={conversionRate} />

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <DailyPerformanceTable
                data={dailyData}
                onExport={handleExportDaily}
                onPageChange={setDailyPage}
              />

              <WeeklyPerformanceTable
                data={metrics?.weekly_data}
                onExport={handleExportWeekly}
              />

              <MonthlyPerformanceTable
                data={metrics?.monthly_data}
                onExport={handleExportMonthly}
              />

              <UTMPerformanceSimple
                appointmentData={metrics?.utm_breakdown}
                patientData={metrics?.utm_breakdown_by_patients}
                performanceData={metrics?.utm_performance}
                trendsData={metrics?.utm_trends}
                comparisonData={metrics?.utm_comparison}
                onExport={handleExportUTM}
              />

              <RecentStartsSimple data={starts} onExport={handleExportStarts} />

              <DashboardLegend />
            </Box>
          </>
        )}
      </DashboardContainer>
    </ThemeProvider>
  )
}

export default PerformanceDashboard
